/**
 * Landing 2026 — Design Tokens
 *
 * Material 3 type scale + monochrome surfaces + brand blue accent.
 * Single source of truth — every landing section reads from here.
 *
 * Type scale (Material 3 inspired, responsive):
 * - displayXl   → Hero H1 (48-72px)
 * - displayLg   → Major section H2 (36-56px)
 * - headlineLg  → Standard section H2 (28-40px)
 * - titleLg     → Card titles (20-22px)
 * - bodyLg      → Sub-text + descriptions (16-18px)
 * - bodyMd      → Body copy (15-16px)
 * - labelMd     → Captions, eyebrows, micro-text (12-14px, uppercase)
 */

export const TYPE = {
  // Fluid type via clamp() — seidenglatte Skalierung iPhone → 4K
  // Hero H1: 40px (Mobile) → 104px (Desktop), Stripe-Skala
  displayXl:
    'text-[clamp(2.5rem,6.5vw,6.5rem)] leading-[1.02] font-semibold tracking-[-0.045em]',
  displayLg:
    'text-[clamp(2rem,4.4vw,3.75rem)] leading-[1.08] font-semibold tracking-[-0.03em]',
  headlineLg:
    'text-[clamp(1.5rem,2.6vw,2.25rem)] leading-[1.15] font-semibold tracking-[-0.015em]',
  titleLg: 'text-lg sm:text-xl font-semibold tracking-[-0.005em]',
  bodyLg:
    'text-[clamp(1rem,1.3vw,1.2rem)] leading-[1.55] text-gray-600',
  bodyMd: 'text-sm sm:text-base leading-relaxed text-gray-600',
  labelMd:
    'text-[11px] sm:text-xs font-medium uppercase tracking-[0.14em]',
  // Einheitliche Section-Subline (impeccable-Audit 2026-07-31)
  sectionSub: 'text-base sm:text-lg leading-[1.6] text-gray-600',
} as const;

export const RADII = {
  card: 'rounded-2xl',
  pill: 'rounded-full',
  inner: 'rounded-xl',
} as const;

export const SURFACE = {
  base: 'bg-white',
  muted: 'bg-gray-50',
  inverted: 'bg-gray-950 text-white',
  card: 'bg-white border border-gray-200/80 shadow-[0_1px_2px_rgba(0,0,0,0.04)]',
  cardMuted: 'bg-gray-50 border border-gray-200/60',
} as const;

// Layered shadows for Stripe-level depth on hero mockups
export const SHADOW = {
  // Subtle elevation for standard cards
  card: 'shadow-[0_1px_2px_rgba(0,0,0,0.04),0_4px_12px_rgba(0,0,0,0.04)]',
  // Big hero mockup — 4-layer shadow for real depth
  hero: 'shadow-[0_1px_2px_rgba(15,23,42,0.06),0_8px_24px_rgba(15,23,42,0.08),0_24px_48px_-12px_rgba(15,23,42,0.12),0_48px_80px_-24px_rgba(15,23,42,0.16)]',
  // Floating sub-element (toast, badge)
  floating: 'shadow-[0_2px_6px_rgba(15,23,42,0.06),0_12px_24px_-8px_rgba(15,23,42,0.16)]',
} as const;

export const ACCENT = {
  bg: 'bg-blue-600',
  bgHover: 'hover:bg-blue-700',
  text: 'text-blue-600',
  textDim: 'text-blue-500',
  tint: 'bg-blue-50',
  border: 'border-blue-600',
  ring: 'focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2',
} as const;

// Handlungs-Akzent NUR für primäre CTAs. Fast-Schwarz statt einer zweiten
// Buntfarbe (Boubkir 2026-07-31: Orange passte nicht zur Marke) — dieselbe
// Lösung wie Linear/Vercel/Stripe: die Hierarchie kommt über HELLIGKEIT,
// nicht über einen zweiten Farbton. Damit bleibt Blau die einzige
// Markenfarbe und der generische "alles-blau"-SaaS-Look ist trotzdem weg.
// gray-950 auf Weiß = 18,7:1 (AAA), deutlich über dem vorherigen
// orange-700 (5,18:1). Emerald bleibt für Geld/Erfolg reserviert.
export const ACTION = {
  bg: 'bg-gray-950',
  bgHover: 'hover:bg-gray-800',
  text: 'text-gray-950',
  tint: 'bg-gray-100',
  ring: 'focus-visible:ring-2 focus-visible:ring-gray-950 focus-visible:ring-offset-2',
} as const;

// Emerald-Regel (impeccable P1 2026-07-31): emerald ist AUSSCHLIESSLICH
// für Geld-Gewinn/Erfolgs-Momente reserviert (Ersparnis-Pill, „Buchung
// erfolgreich", Setup-komplett). Nie als dritter Deko-Akzent neben
// Blau (Marke) und Fast-Schwarz (CTA) einsetzen.
export const SUCCESS = {
  bg: 'bg-emerald-500',
  text: 'text-emerald-700',
  tint: 'bg-emerald-50',
} as const;

export const SPACING = {
  container: 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8',
  section: 'py-16 sm:py-24 lg:py-28',
  sectionTight: 'py-12 sm:py-16 lg:py-20',
  stackLg: 'space-y-6 sm:space-y-8',
  stackMd: 'space-y-4 sm:space-y-6',
} as const;

export const MOTION = {
  // All transitions <250ms, respect prefers-reduced-motion via Tailwind's motion-safe variants
  fadeIn: 'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-500',
  hover: 'transition-all duration-200',
} as const;

export const TOUCH = {
  // ≥44px iOS / 48dp Material — never go below
  cta: 'h-12 sm:h-14',
  ctaSm: 'h-11',
  iconBtn: 'h-11 w-11',
} as const;
